import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, ArrowRight, CheckCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { programsData } from '../data/mock';

const Programs = () => {
  const { programId } = useParams();

  // If programId exists, show single program detail
  if (programId) {
    const program = programsData.find(p => p.id === programId);
    if (!program) {
      return (
        <div className="min-h-screen pt-20 flex items-center justify-center">
          <div className="text-center">
            <h1 className="text-4xl font-bold text-gray-900 mb-4">Program Not Found</h1>
            <Link to="/programs">
              <Button>View All Programs</Button>
            </Link>
          </div>
        </div>
      );
    }

    return (
      <div className="min-h-screen pt-20">
        {/* Hero Section */}
        <section className="relative h-[60vh] overflow-hidden">
          <img loading="lazy"
            src={program.image}
            alt={program.name}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-gray-900/95 via-gray-900/80 to-gray-900/60"></div>
          <div className="absolute inset-0 flex items-center">
            <div className="container mx-auto px-4">
              <Link to="/programs">
                <Button variant="ghost" className="text-white hover:text-orange-400 mb-6">
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back to All Programs
                </Button>
              </Link>
              <div className="max-w-3xl">
                <h1 className="text-5xl md:text-6xl font-bold text-white mb-4">
                  {program.name}
                </h1>
                <p className="text-2xl text-orange-300 font-medium mb-6">
                  {program.tagline}
                </p>
                <p className="text-xl text-gray-200 leading-relaxed">
                  {program.description}
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Program Details */}
        <section className="py-20 bg-white">
          <div className="container mx-auto px-4">
            <div className="max-w-5xl mx-auto">
              {/* Stats Cards */}
              <div className="grid md:grid-cols-3 gap-6 mb-16">
                {Object.entries(program.stats).map(([key, value], index) => (
                  <Card key={index} className="border-0 shadow-lg">
                    <CardContent className="p-6 text-center">
                      <div className="text-4xl font-bold text-orange-600 mb-2">
                        {value}
                      </div>
                      <div className="text-sm text-gray-600 font-medium uppercase">
                        {key.replace(/([A-Z])/g, ' $1').trim()}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>

              {/* Features */}
              <div className="mb-16">
                <h2 className="text-3xl font-bold text-gray-900 mb-8">
                  Program Services & Support
                </h2>
                <div className="grid md:grid-cols-2 gap-4">
                  {program.features.map((feature, index) => (
                    <div key={index} className="flex items-start space-x-3 p-4 rounded-lg hover:bg-orange-50 transition-colors duration-200">
                      <CheckCircle className="w-6 h-6 text-orange-600 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-700">{feature}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Partner Organizations */}
              <div className="bg-gradient-to-br from-orange-50 to-amber-50 rounded-2xl p-8">
                <h3 className="text-2xl font-bold text-gray-900 mb-6">
                  Partner Organizations
                </h3>
                <div className="space-y-3">
                  {program.partnerOrgs.map((org, index) => (
                    <div key={index} className="flex items-center space-x-3">
                      <div className="w-2 h-2 bg-orange-600 rounded-full"></div>
                      <span className="text-gray-700 font-medium">{org}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* CTA Section */}
              <div className="mt-16 text-center">
                <h3 className="text-3xl font-bold text-gray-900 mb-6">
                  Ready to Get Started?
                </h3>
                <p className="text-lg text-gray-600 mb-8 max-w-2xl mx-auto">
                  If you or someone you know could benefit from this program, we're here to help.
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <Link to="/get-help">
                    <Button 
                      size="lg"
                      className="bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700 text-white font-semibold shadow-lg"
                    >
                      Apply for Services
                      <ArrowRight className="ml-2 w-5 h-5" />
                    </Button>
                  </Link>
                  <Link to="/contact">
                    <Button 
                      size="lg"
                      variant="outline"
                      className="border-2 border-orange-600 text-orange-600 hover:bg-orange-600 hover:text-white font-semibold"
                    >
                      Contact Us
                    </Button>
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>
    );
  }

  // Show all programs overview
  return (
    <div className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="py-24 bg-gradient-to-br from-orange-50 via-amber-50 to-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6">
              Our <span className="text-orange-600">Programs</span>
            </h1>
            <p className="text-xl text-gray-600 leading-relaxed">
              Four comprehensive programs addressing the most critical needs in our community, 
              providing holistic support for lasting transformation.
            </p>
          </div>
        </div>
      </section>

      {/* Programs Grid */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto space-y-16">
            {programsData.map((program, index) => (
              <Card 
                key={program.id}
                className="border-0 shadow-xl overflow-hidden hover:shadow-2xl transition-shadow duration-300"
              >
                <div className={`grid md:grid-cols-2 gap-0 ${index % 2 === 1 ? 'md:grid-flow-dense' : ''}`}>
                  <div className={`relative h-96 md:h-auto ${index % 2 === 1 ? 'md:col-start-2' : ''}`}>
                    <img loading="lazy"
                      src={program.image}
                      alt={program.name}
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-gray-900/60 to-transparent"></div>
                  </div>
                  <CardContent className={`p-8 md:p-12 flex flex-col justify-center ${index % 2 === 1 ? 'md:col-start-1' : ''}`}>
                    <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-3">
                      {program.name}
                    </h2>
                    <p className="text-lg text-orange-600 font-semibold mb-6">
                      {program.tagline}
                    </p>
                    <p className="text-gray-600 leading-relaxed mb-6">
                      {program.description}
                    </p>

                    {/* Quick Stats */}
                    <div className="grid grid-cols-3 gap-4 mb-8 p-4 bg-gray-50 rounded-lg">
                      {Object.entries(program.stats).map(([key, value], statIndex) => (
                        <div key={statIndex} className="text-center">
                          <div className="text-2xl font-bold text-orange-600">
                            {value.split(' ')[0]}
                          </div>
                          <div className="text-xs text-gray-600 mt-1">
                            {value.split(' ').slice(1).join(' ') || key}
                          </div>
                        </div>
                      ))}
                    </div>

                    <Link to={`/programs/${program.id}`}>
                      <Button 
                        className="bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700 text-white font-semibold w-full md:w-auto"
                      >
                        Learn More About This Program
                        <ArrowRight className="ml-2 w-4 h-4" />
                      </Button>
                    </Link>
                  </CardContent>
                </div>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-orange-600 to-amber-600 text-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              Need Help? We're Here for You
            </h2>
            <p className="text-xl mb-8 text-orange-100">
              Don't navigate this journey alone. Our team is ready to connect you 
              with the right program and support services.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/get-help">
                <Button 
                  size="lg"
                  className="bg-white text-orange-600 hover:bg-gray-100 font-semibold text-lg px-8 shadow-xl"
                >
                  Apply for Services
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
              <Link to="/contact">
                <Button 
                  size="lg"
                  variant="outline"
                  className="border-2 border-white text-white hover:bg-white hover:text-orange-600 font-semibold text-lg px-8"
                >
                  Contact Us
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Programs;
